import { useEffect } from 'react';
import Navbar from '../../../components/feature/Navbar';
import Footer from '../../../components/feature/Footer';
import WhatsAppButton from '../../../components/feature/WhatsAppButton';
import ScrollToTopButton from '../../../components/feature/ScrollToTopButton';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';

const SCHEMA_PREVENCAO = JSON.stringify({
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'Service',
      '@id': `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio#service`,
      name: 'Engenharia de Prevenção de Incêndio — AVCB e CLCB',
      description: 'Projetos AVCB, CLCB, regularização junto ao Corpo de Bombeiros, laudos elétricos, SPDA, estanqueidade GLP/GN e treinamento de brigada em Sorocaba e Campinas.',
      provider: {
        '@type': 'LocalBusiness',
        name: 'Engeporto Engenharia e Serviços Ltda.',
        url: import.meta.env.VITE_SITE_URL,
      },
      areaServed: ['Sorocaba', 'Campinas', 'Porto Feliz'],
      serviceType: 'Engenharia de Prevenção de Incêndio',
      url: `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio`,
    },
    {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Início', item: import.meta.env.VITE_SITE_URL },
        { '@type': 'ListItem', position: 2, name: 'Serviços', item: `${import.meta.env.VITE_SITE_URL}/servicos` },
        { '@type': 'ListItem', position: 3, name: 'Prevenção de Incêndio', item: `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio` },
      ],
    },
  ],
});

const PrevencaoIncendioPage = () => {
  useEffect(() => {
    document.title = 'Prevenção de Incêndio Sorocaba Campinas | AVCB, CLCB e Laudos — Engeporto';
    const setMeta = (name: string, content: string, prop = false) => {
      const sel = prop ? `meta[property="${name}"]` : `meta[name="${name}"]`;
      const el = document.querySelector(sel) as HTMLMetaElement | null;
      if (el) el.setAttribute('content', content);
    };
    setMeta('description', 'Projetos AVCB e CLCB, regularização junto ao Corpo de Bombeiros, laudos elétricos, SPDA e treinamento de brigada de incêndio nas Regiões Metropolitanas de Sorocaba e Campinas. Engeporto Engenharia.');
    setMeta('keywords', 'AVCB Sorocaba, CLCB Campinas, prevenção incêndio, brigada incêndio, laudo elétrico, SPDA, regularização bombeiros');
    setMeta('og:title', 'Prevenção de Incêndio | AVCB, CLCB e Laudos — Engeporto Sorocaba Campinas', true);
    setMeta('og:description', 'Projetos AVCB, CLCB, laudos elétricos e treinamento de brigada em Sorocaba e Campinas.', true);
    setMeta('og:url', `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio`, true);

    // Canonical
    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL}/servicos/prevencao-incendio`);
    return () => { canonical?.remove(); };
  }, []);

  const servicos = [
    {
      icon: 'ri-file-text-line',
      title: 'Projetos AVCB e CLCB',
      description: 'Elaboração completa de projetos técnicos para emissão e renovação de Auto de Vistoria do Corpo de Bombeiros (AVCB) e Certificado de Licença do Corpo de Bombeiros (CLCB).',
    },
    {
      icon: 'ri-government-line',
      title: 'Regularização junto ao Corpo de Bombeiros',
      description: 'Assessoria completa no processo de regularização, desde a análise inicial até a obtenção do licenciamento.',
    },
    {
      icon: 'ri-lightbulb-flash-line',
      title: 'Consultoria Técnica em Segurança Contra Incêndio',
      description: 'Análise técnica de edificações, identificação de não conformidades e elaboração de planos de adequação.',
    },
    {
      icon: 'ri-file-list-3-line',
      title: 'Elaboração de Memoriais e Documentação Técnica',
      description: 'Desenvolvimento de memoriais descritivos, especificações técnicas e plantas com indicação de sistemas de proteção.',
    },
    {
      icon: 'ri-tools-line',
      title: 'Dimensionamento de Sistemas de Proteção',
      description: 'Cálculo e especificação de sistemas de hidrantes, sprinklers, iluminação de emergência e extintores.',
    },
    {
      icon: 'ri-team-line',
      title: 'Treinamentos de Brigada de Incêndio',
      description: 'Cursos básicos e intermediários de formação de brigadistas, incluindo teoria e prática de combate a incêndio.',
    },
    {
      icon: 'ri-flashlight-line',
      title: 'Laudos Técnicos em Instalações Elétricas',
      description: 'Inspeções e avaliações técnicas de instalações elétricas, análise da demanda energética e verificação de conformidade com NBR 5410 e NBR 14039.',
    },
    {
      icon: 'ri-flashlight-fill',
      title: 'Inspeção e Manutenção do Para-Raios (SPDA)',
      description: 'Avaliação completa do Sistema de Proteção contra Descargas Atmosféricas e sistemas de aterramento, com emissão de laudos técnicos em total conformidade com a NBR 5419',
    },
    {
      icon: 'ri-file-shield-2-line',
      title: 'Inspeção e Ensaio de Estanqueidade (GLP e GN)',
      description: 'Testes de estanqueidade para detecção de vazamentos em tubulações, com emissão de laudo técnico e ART, seguindo as diretrizes da NBR 15526 e NBR 15358.',
    },
  ];

  const beneficios = [
    'Conformidade total com legislação vigente',
    'Redução de riscos de incêndio',
    'Proteção de vidas e patrimônio',
    'Regularização legal da edificação',
    'Valorização do imóvel',
    'Tranquilidade e segurança',
  ];

  return (
    <div className="min-h-screen bg-white">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: SCHEMA_PREVENCAO }}
      />
      <Navbar />
      
      <section className="pt-36 md:pt-32 pb-12 md:pb-20 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Link
              to="/servicos"
              className="inline-flex items-center gap-2 text-gray-300 hover:text-white mb-4 md:mb-8 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              Voltar para Serviços
            </Link>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
              <div>
                <div className="inline-flex items-center gap-3 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-bold mb-6">
                  <i className="ri-fire-line text-lg"></i>
                  PILAR 1
                </div>
                
                <h1 className="font-display font-bold text-2xl md:text-5xl lg:text-6xl text-white mb-4 md:mb-6 leading-tight">
                  Engenharia de Prevenção de Incêndio
                </h1>
                
                <p className="text-sm md:text-xl text-gray-300 leading-relaxed">
                  Soluções completas em projetos AVCB, CLCB, eletrotécnica aplicada e consultoria especializada em sistemas de proteção contra incêndio
                </p>
              </div>

              <div className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl">
                <img
                  src="https://public.readdy.ai/ai/img_res/d941ba4234e13f0439513457eb144343.jpg"
                  alt="Prevenção de Incêndio"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Serviços Especializados
            </h2>
            <p className="text-sm md:text-lg text-gray-600 max-w-3xl mx-auto">
              Portfólio completo de soluções em prevenção e combate a incêndio, incluindo eletrotécnica aplicada
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-8">
            {servicos.map((servico, index) => (
              <motion.div
                key={servico.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05, duration: 0.5 }}
                className="bg-gray-50 rounded-xl p-5 md:p-8 hover:shadow-xl transition-all border border-gray-100"
              >
                <div className="w-10 h-10 md:w-14 md:h-14 flex items-center justify-center bg-[#D4AF37]/10 rounded-xl mb-4 md:mb-6">
                  <i className={`${servico.icon} text-xl md:text-2xl text-[#D4AF37]`}></i>
                </div>
                <h3 className="font-display font-bold text-base md:text-xl text-[#0A1628] mb-3">
                  {servico.title}
                </h3>
                <p className="text-gray-600 leading-relaxed text-sm">
                  {servico.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 md:gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4 md:mb-6">
                Benefícios para Sua Empresa
              </h2>
              <p className="text-sm md:text-lg text-gray-700 leading-relaxed mb-6 md:mb-8">
                Investir em prevenção de incêndio vai além da conformidade legal. É garantir a segurança de pessoas, proteger o patrimônio e demonstrar responsabilidade empresarial.
              </p>

              <div className="space-y-4">
                {beneficios.map((beneficio) => (
                  <div key={beneficio} className="flex items-start gap-4">
                    <div className="w-6 h-6 flex items-center justify-center bg-[#D4AF37] rounded-full flex-shrink-0 mt-1">
                      <i className="ri-check-line text-white text-sm"></i>
                    </div>
                    <span className="text-gray-700 text-sm md:text-lg">{beneficio}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="aspect-[4/3] rounded-2xl overflow-hidden shadow-2xl"
            >
              <img
                src="https://readdy.ai/api/search-image?query=professional%20fire%20safety%20engineer%20inspecting%20fire%20extinguisher%20and%20emergency%20equipment%20in%20modern%20commercial%20building%20wearing%20business%20attire%20with%20clipboard%20conducting%20safety%20inspection%20bright%20clean%20environment&width=800&height=600&seq=prev002&orientation=landscape"
                alt="Benefícios"
                className="w-full h-full object-cover object-top"
              />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-8 md:mb-16"
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-[#0A1628] mb-4">
              Nosso Processo
            </h2>
            <p className="text-sm md:text-lg text-gray-600">
              Metodologia estruturada para garantir resultados eficientes
            </p>
          </motion.div>

          <div className="space-y-4 md:space-y-6">
            {[
              {
                numero: '01',
                titulo: 'Análise Inicial Gratuita',
                descricao: 'Visita técnica para levantamento de dados, análise da edificação e identificação das necessidades específicas.',
              },
              {
                numero: '02',
                titulo: 'Elaboração do Projeto',
                descricao: 'Desenvolvimento do projeto técnico simplificado ou completo, incluindo memoriais, plantas e especificações de sistemas.',
              },
              {
                numero: '03',
                titulo: 'Aprovação e Protocolo',
                descricao: 'Protocolo de análise da documentação junto ao Corpo de Bombeiros e acompanhamento do processo de aprovação.',
              },
              {
                numero: '04',
                titulo: 'Vistoria Final e Aprovação',
                descricao: 'Acompanhamento de todo o processo até a obtenção do AVCB ou CLCB, garantindo a conformidade total.',
              },
            ].map((etapa, index) => (
              <motion.div
                key={etapa.numero}
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="flex items-start gap-4 md:gap-6 bg-gray-50 rounded-xl p-5 md:p-8 border border-gray-100"
              >
                <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center bg-[#D4AF37] text-[#0A1628] rounded-xl font-display font-bold text-lg md:text-xl flex-shrink-0">
                  {etapa.numero}
                </div>
                <div className="flex-1">
                  <h3 className="font-display font-bold text-lg md:text-2xl text-[#0A1628] mb-2">
                    {etapa.titulo}
                  </h3>
                  <p className="text-gray-700 leading-relaxed text-sm md:text-base">
                    {etapa.descricao}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 md:py-24 bg-gradient-to-br from-[#0A1628] via-[#0D1B33] to-[#0A1628] relative overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, white 1px, transparent 0)`,
            backgroundSize: '40px 40px'
          }}></div>
        </div>
        
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="font-display font-bold text-2xl md:text-4xl lg:text-5xl text-white mb-4 md:mb-6">
              Precisa Regularizar seu AVCB?
            </h2>
            <p className="text-sm md:text-xl text-gray-300 mb-6 md:mb-10">
              Fale com nossos especialistas e receba uma consultoria personalizada
            </p>
            <a
              href={`https://wa.me/5515996719308?text=${encodeURIComponent('Olá! Tenho interesse em serviços de Prevenção de Incêndio (AVCB/CLCB). Gostaria de solicitar um orçamento.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 bg-[#D4AF37] text-[#0A1628] px-6 py-4 md:px-10 md:py-5 rounded-lg text-base md:text-lg font-bold hover:bg-[#C4A030] transition-all shadow-xl whitespace-nowrap cursor-pointer"
            >
              <i className="ri-whatsapp-line text-xl md:text-2xl"></i>
              Solicitar Orçamento
            </a>
          </motion.div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
      <ScrollToTopButton />
    </div>
  );
};

export default PrevencaoIncendioPage;